import React from 'react'
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
} from '@mui/material'
import { Close } from '@mui/icons-material'
import type { ImportRecord } from '@app/src/types/tabularImport'
import { parseExtractedData } from '@app/src/types/tabularImport'

interface RowDetailModalProps {
  record: ImportRecord | null
  open: boolean
  onClose: () => void
}

const RowDetailModal: React.FC<RowDetailModalProps> = ({ record, open, onClose }) => {
  if (!record) return null

  const extractedData = parseExtractedData(record.extractedData)
  const hasErrors = record.validationErrors.length > 0

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6">Row #{record.rowNumber} Details</Typography>
            <Chip
              label={record.status}
              size="small"
              color={record.status === 'Success' ? 'success' : record.status === 'Failed' ? 'error' : 'default'}
            />
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {hasErrors && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Validation Errors:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {record.validationErrors.map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}

        {extractedData ? (
          <Grid container spacing={3}>
            {/* Raw CSV Values */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Raw Source Values
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Column</TableCell>
                      <TableCell>Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(extractedData.rawValues).map(([column, value]) => (
                      <TableRow key={`${column}-${record.rowNumber}`}>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {column}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {value || <span style={{ color: 'gray', fontStyle: 'italic' }}>(empty)</span>}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            {/* Extracted entity fields */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Extracted Fields
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Field</TableCell>
                      <TableCell>Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(extractedData.extractedFields).map(([field, value]) => (
                      <TableRow key={`${field}-${record.rowNumber}`}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {field}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {value || <span style={{ color: 'gray', fontStyle: 'italic' }}>(empty)</span>}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        ) : (
          <Typography color="text.secondary" textAlign="center" py={4}>
            No extracted data available for this row
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default RowDetailModal
